import { ReactNode, useState, useEffect, useRef, useCallback } from "react";
import { AuthenticateWithRedirectCallback, useClerk, useUser } from "@clerk/react";
// The classic resource API (signIn.create / setActive / authenticateWithRedirect)
// lives in the /legacy entrypoint — @clerk/react v6's own useSignIn is the
// signals API, which has neither setActive nor createdSessionId.
import { useSignIn } from "@clerk/react/legacy";
import { useAction, useMutation, useQuery } from "convex/react";
import { ConvexHttpClient } from "convex/browser";
import { openUrl } from "@tauri-apps/plugin-opener";
import { api } from "../lib/convexApi";
import { isTauri } from "../services/nativeBrowser";

// ── Desktop browser-auth handoff — the "passkeys don't work in the app" fix ──
//
// A third-party app's embedded WebView (Tauri/WKWebView) cannot use the local
// platform passkey for an external IdP like Google (platform passkeys are
// bound to the relying party's associated domain, which only the system
// browsers have for google.com). So in-app Google sign-in falls back to the
// cross-device "Bluetooth" passkey flow, which fails.
//
// Fix: the app shows the familiar provider buttons (Google / Apple / GitHub),
// but each one runs sign-in in the SYSTEM BROWSER (where the user's passkey
// works) — the opened tab auto-launches that provider's OAuth flow — then the
// session transfers back via a one-time Clerk sign-in token keyed by an
// unguessable, app-generated requestId. See convex/auth/desktopAuth.ts.

const PARAM = "desktopAuth";
const PROVIDER_PARAM = "provider";
const SSO_PARAM = "ssoCallback";
// Marks the tab as having JUST completed an explicit OAuth leg (set on
// redirectUrlComplete). Distinguishes "the user picked an account at the
// provider seconds ago" (auto-mint) from "the browser happened to already
// have a Clerk session" (must confirm — silently binding the app to whatever
// account the browser was signed into surprises multi-account users).
const COMPLETED_PARAM = "completed";

// Cadence of the app-side HTTP fallback poll for the minted ticket (the
// reactive subscription is the fast path; see the poll effect in
// DesktopProviderSignIn). Well under the 5-minute ticket TTL.
const TICKET_POLL_MS = 3_000;

// How long the ticket-row cleanup may hold up `setActive`. Deleting the row is
// housekeeping; being signed in is the point. See the claim effect below.
const CONSUME_TIMEOUT_MS = 3_000;

// The pending handoff, persisted so an app RELOAD does not abandon it.
//
// requestId used to live only in React state. That made the whole handoff
// depend on the WebView staying alive for the entire browser round-trip — and
// it does not: WKWebView reclaims a backgrounded page (the poll effect below
// already works around a related suspension bug), a Tauri restart drops it, and
// so does any HMR reload in dev. When that happened the browser still minted a
// ticket and still said "Signed in", but the app had forgotten which requestId
// it was waiting for, so it polled for nothing and the ticket sat unclaimed
// until its 5-minute TTL expired. Three such orphans were found on the
// production deployment, all status "ready", all never consumed — that is what
// this bug looks like from the outside: sign-in that silently never completes,
// with no error anywhere.
//
// sessionStorage, not localStorage: a pending handoff should not outlive the
// app session. Stale entries are also rejected on read by the same TTL the
// server enforces, so a resume can never present a ticket the server would
// refuse.
const PENDING_KEY = "steerly.desktopAuth.pending";
const PENDING_TTL_MS = 5 * 60_000; // mirrors TICKET_TTL_MS in convex/auth/desktopAuth.ts

type PendingHandoff = { requestId: string; startedAt: number; name: string | null };

function readPendingHandoff(): PendingHandoff | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PendingHandoff;
    if (!p?.requestId || typeof p.startedAt !== "number") return null;
    // Past the server's TTL the ticket is unusable — resuming would just poll
    // until the user gave up. Report it as absent, but do NOT evict here: this
    // runs inside a useState initialiser, and mutating storage during the
    // render phase is impure (React 18 StrictMode invokes initialisers twice).
    // The entry is harmless once ignored, and the next start() overwrites it;
    // the effect below evicts it properly.
    if (Date.now() - p.startedAt > PENDING_TTL_MS) return null;
    return p;
  } catch {
    return null; // storage disabled/full — degrade to the old in-memory behaviour
  }
}

// Drop any pending handoff. Called when the app becomes SIGNED IN by any route,
// because at that point no handoff is relevant any more — and leaving one behind
// is not merely untidy, it is an auth hazard:
//
//   start a handoff → complete it in the browser (ticket minted) → the app
//   happens to sign in another way (a restored session) so the claim effect
//   never runs → the entry survives → the user later SIGNS OUT → the sign-in
//   card mounts, resumes the stale handoff, claims the still-live ticket, and
//   signs them straight back in without being asked.
//
// On a shared machine that lands on whichever account minted the ticket, not
// whoever is sitting there. A deliberate sign-out must stay signed out.
export function clearPendingDesktopHandoff(): void {
  writePendingHandoff(null);
}

function writePendingHandoff(p: PendingHandoff | null): void {
  try {
    if (p) sessionStorage.setItem(PENDING_KEY, JSON.stringify(p));
    else sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* non-fatal: the in-memory path still works for an app that never reloads */
  }
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.13-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor">
      <path d="M16.36 12.76c-.02-2.05 1.67-3.03 1.75-3.08-.95-1.39-2.44-1.58-2.97-1.6-1.26-.13-2.47.74-3.11.74-.64 0-1.63-.72-2.68-.7-1.38.02-2.65.8-3.36 2.03-1.43 2.48-.37 6.15 1.03 8.17.68.99 1.49 2.1 2.56 2.06 1.03-.04 1.42-.66 2.66-.66 1.24 0 1.59.66 2.68.64 1.11-.02 1.81-1 2.48-2 .78-1.15 1.1-2.26 1.12-2.32-.02-.01-2.14-.82-2.16-3.28z" />
      <path d="M14.31 6.74c.57-.69.95-1.65.85-2.6-.82.03-1.81.55-2.4 1.23-.53.61-.99 1.59-.86 2.52.91.07 1.84-.46 2.41-1.15z" />
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true" fill="currentColor">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
    </svg>
  );
}

const PROVIDERS = [
  { strategy: "oauth_google", name: "Google", Icon: GoogleIcon },
  { strategy: "oauth_apple", name: "Apple", Icon: AppleIcon },
  { strategy: "oauth_github", name: "GitHub", Icon: GitHubIcon },
] as const;

export type DesktopProviderStrategy = (typeof PROVIDERS)[number]["strategy"];

function newRequestId(): string {
  // This is the bearer secret binding the app instance to the browser tab, so
  // it MUST be cryptographically random — never Math.random(). crypto is
  // universally available in modern browsers and the Tauri WebView; throw
  // rather than silently degrade to a guessable id (session-hijack risk).
  if (typeof crypto === "undefined" || !crypto.randomUUID || !crypto.getRandomValues) {
    throw new Error("Web Crypto API unavailable — cannot mint a secure desktop-auth request id");
  }
  const uuid = crypto.randomUUID();
  const extra = Array.from(crypto.getRandomValues(new Uint8Array(8)))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `${uuid}-${extra}`;
}

function readDesktopAuthParam(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(PARAM);
}

// Which side of the handoff are we on?
//   "app"     — running inside the Tauri desktop app (opens the browser, polls)
//   "browser" — a system-browser tab opened by the app (?desktopAuth=… present)
//   null      — ordinary web usage
export function desktopAuthRole(): "app" | "browser" | null {
  if (isTauri()) return "app";
  if (readDesktopAuthParam()) return "browser";
  return null;
}

// The provider the app pre-selected for the browser tab (?provider=oauth_…).
// Whitelisted against PROVIDERS — an unknown value is treated as absent.
export function desktopAuthProvider(): DesktopProviderStrategy | null {
  if (typeof window === "undefined") return null;
  const p = new URLSearchParams(window.location.search).get(PROVIDER_PARAM);
  const known = PROVIDERS.find((x) => x.strategy === p);
  return known ? known.strategy : null;
}

// True while the browser tab is on the OAuth return leg (?ssoCallback=1) and
// Clerk still has to exchange the callback for a session.
export function desktopSsoCallbackPending(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get(SSO_PARAM) === "1";
}

function justCompletedOauth(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get(COMPLETED_PARAM) === "1";
}

// ── App side ─────────────────────────────────────────────────────────────
// The desktop sign-in card: provider buttons that open the system browser to
// this same app (served by the host) with ?desktopAuth=<requestId> and the
// chosen provider, then poll Convex for the minted ticket and complete the
// session in-app via the `ticket` strategy.
export function DesktopProviderSignIn() {
  const { signIn, setActive } = useSignIn();
  // Seed from sessionStorage so an app reload mid-handoff RESUMES the wait
  // instead of silently abandoning it (see PENDING_KEY above). Read once, in
  // the initialiser, so the very first render is already in the waiting state
  // and the poll effect starts immediately — no flash of the provider buttons.
  // Lazy useState initialiser, NOT useRef(readPendingHandoff()): the argument to
  // useRef is evaluated on every render even though only the first is kept, and
  // readPendingHandoff MUTATES storage (it removes an entry past the TTL). That
  // would be a side effect during the render phase, on every render.
  const [resumed] = useState<PendingHandoff | null>(() => readPendingHandoff());
  // Evict an expired entry here rather than inside the reader — a side effect
  // belongs in an effect, not in a render-phase initialiser.
  useEffect(() => {
    if (!resumed) writePendingHandoff(null);
  }, [resumed]);
  const [requestId, setRequestId] = useState<string | null>(resumed?.requestId ?? null);
  const [status, setStatus] = useState<"idle" | "waiting" | "completing" | "error">(
    resumed ? "waiting" : "idle"
  );
  const [pendingName, setPendingName] = useState<string | null>(resumed?.name ?? null);
  const [error, setError] = useState<string | null>(null);
  const consume = useMutation(api.auth.desktopAuth.consumeTicket);
  const completedRef = useRef(false);

  const poll = useQuery(
    api.auth.desktopAuth.awaitTicket,
    requestId && status === "waiting" ? { requestId } : "skip"
  ) as { ready: boolean; ticket: string | null } | undefined;

  // WS-independent fallback for the ticket wait. If the app sat backgrounded
  // long enough for WebKit to suspend the page, the shared Convex client can
  // come back with a zombie WebSocket — the socket is gone but no close event
  // ever fired, so the client still believes it's connected and the reactive
  // subscription above never delivers (confirmed live in the v1.0.19 QA).
  // Plain HTTP queries open fresh connections and don't care, so poll the
  // same query while waiting. background_throttling(Disabled) in the Tauri
  // shell prevents the suspension itself on macOS 14+; this covers the rest.
  // The polled ticket carries the requestId it was polled FOR: clearInterval
  // can't reach a query already in flight, so a response resolving after
  // cancel → restart would otherwise surface request A's ticket into request
  // B's claim. The `cancelled` flag drops those late responses, and the
  // {requestId, ticket} pairing lets the claim below require an exact match.
  const [polledTicket, setPolledTicket] = useState<{ requestId: string; ticket: string } | null>(
    null
  );
  useEffect(() => {
    if (status !== "waiting" || !requestId) return;
    // Read the env directly (NOT via lib/convex, whose module-load throw on a
    // missing URL would take down env-less test runs that import this file).
    const url = import.meta.env.VITE_CONVEX_URL as string | undefined;
    if (!url) return;
    const http = new ConvexHttpClient(url);
    let cancelled = false;
    const timer = setInterval(() => {
      void http
        .query(api.auth.desktopAuth.awaitTicket, { requestId })
        .then((res) => {
          if (!cancelled && res.ready && res.ticket)
            setPolledTicket({ requestId, ticket: res.ticket });
        })
        .catch(() => {}); // transient network error — keep polling
    }, TICKET_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [status, requestId]);

  const start = useCallback(async (strategy?: DesktopProviderStrategy, name?: string) => {
    setError(null);
    let id: string;
    try {
      id = newRequestId();
    } catch (e) {
      setStatus("error");
      setError(e instanceof Error ? e.message : "Could not start sign-in.");
      return;
    }
    setRequestId(id);
    setPendingName(name ?? null);
    setPolledTicket(null);
    completedRef.current = false;
    // Persist BEFORE opening the browser: the reload we are guarding against
    // can happen the instant focus leaves the app.
    writePendingHandoff({ requestId: id, startedAt: Date.now(), name: name ?? null });
    // Open the web app in the system browser, in browser-role. Prefer an
    // explicit VITE_WEB_APP_URL when configured; otherwise use the window
    // origin. The desktop shell loads the web app over http (the Bun host
    // origin, e.g. http://127.0.0.1:3137 — reachable by the system browser
    // on the same machine), NOT the tauri:// asset scheme, so the origin
    // fallback is a real, browser-loadable URL. The env override exists for
    // builds that point the WebView at a hosted origin.
    const base = (import.meta.env.VITE_WEB_APP_URL as string | undefined) || window.location.origin;
    const url =
      `${base.replace(/\/$/, "")}/?${PARAM}=${encodeURIComponent(id)}` +
      (strategy ? `&${PROVIDER_PARAM}=${encodeURIComponent(strategy)}` : "");
    try {
      await openUrl(url);
      setStatus("waiting");
    } catch {
      setStatus("error");
      setError("Could not open your browser. Copy this URL manually:\n" + url);
    }
  }, []);

  const cancel = useCallback(() => {
    setStatus("idle");
    setRequestId(null);
    setPendingName(null);
    setPolledTicket(null);
    writePendingHandoff(null);
  }, []);

  // The ticket can arrive on either channel — the reactive subscription (fast
  // path) or the HTTP poll (fallback). Whichever lands first wins;
  // completedRef makes the claim idempotent. The poll result only counts when
  // it was polled for the CURRENT requestId (see the race note above; the
  // subscription is inherently correlated because its args carry requestId).
  const ticket =
    (status === "waiting" &&
      ((poll?.ready && poll.ticket) ||
        (polledTicket?.requestId === requestId ? polledTicket.ticket : null))) ||
    null;

  useEffect(() => {
    if (!ticket || !requestId) return;
    // Clerk may still be loading when the ticket lands. Previously this fell
    // through to `throw new Error("Clerk not ready")`, whose catch cleared the
    // persisted handoff — permanently losing a perfectly good ticket and
    // reproducing the exact symptom this PR exists to fix. Waiting is correct:
    // signIn/setActive are effect deps, so this re-runs when Clerk is ready.
    if (!signIn || !setActive) return;
    if (completedRef.current) return;
    completedRef.current = true;
    setStatus("completing");
    (async () => {
      try {
        if (!signIn || !setActive) throw new Error("Clerk not ready");
        const res = await signIn.create({ strategy: "ticket", ticket });
        writePendingHandoff(null); // claimed — nothing left to resume
        // Delete the row BEFORE setActive, and await it. setActive flips the
        // app into the signed-in tree, which unmounts this card — a
        // fire-and-forget mutation issued after that is routinely dropped, so
        // the row survived every successful claim. Measured on the production
        // deployment: a ticket the desktop app had definitely spent (Clerk
        // refused to redeem it a second time, 233s before its expiry) still
        // sat there status "ready". That made "rows sit ready forever" look
        // like proof sign-in never completed, when it proves nothing at all —
        // it cost hours of misdirected debugging. Never throws, so a failed
        // cleanup cannot turn a successful sign-in into an error.
        // ...but bounded. Awaiting it outright traded a leaked row for a worse
        // failure: a Convex client whose socket died can leave a mutation
        // promise PENDING rather than rejecting it (the zombie-WebSocket case
        // the poll fallback above already exists for), and `.catch()` never
        // runs on a promise that never settles — so the user would sit on
        // "Signing you in…" forever with a perfectly good session in hand.
        // Cleanup is best-effort; sign-in is not.
        await Promise.race([
          consume({ requestId }).catch(() => {}),
          new Promise((resolve) => setTimeout(resolve, CONSUME_TIMEOUT_MS)),
        ]);
        await setActive({ session: res.createdSessionId });
        // setActive flips the app into the signed-in tree automatically.
      } catch (e) {
        completedRef.current = false;
        setStatus("error");
        setError(e instanceof Error ? e.message : "Sign-in handoff failed. Try again.");
        // Also clear on failure: this ticket is spent (Clerk tickets are
        // single-use), so resuming it after a reload would retry something that
        // can only fail again. The user retries via the buttons.
        writePendingHandoff(null);
      }
    })();
  }, [ticket, requestId, signIn, setActive, consume]);

  const busy = status === "waiting" || status === "completing";

  return (
    <div className="desktop-auth">
      <h1 className="desktop-auth__title">Sign in to Steerly</h1>
      {busy ? (
        <>
          <p className="desktop-auth__status" role="status" aria-live="polite">
            {status === "completing"
              ? "Signing you in…"
              : pendingName
                ? `Finish signing in with ${pendingName} in your browser`
                : "Finish signing in in your browser"}
          </p>
          <p className="desktop-auth__hint">You’ll be signed in here automatically.</p>
          {status === "waiting" ? (
            <button type="button" className="desktop-auth__cancel" onClick={cancel}>
              Cancel
            </button>
          ) : null}
        </>
      ) : (
        <>
          <p className="desktop-auth__hint">
            Sign-in opens in your browser — passkeys work there — then returns you here.
          </p>
          <div className="desktop-auth__providers">
            {PROVIDERS.map(({ strategy, name, Icon }) => (
              <button
                key={strategy}
                type="button"
                className="desktop-auth__provider"
                onClick={() => void start(strategy, name)}
              >
                <Icon />
                <span>Continue with {name}</span>
              </button>
            ))}
            <button
              type="button"
              className="desktop-auth__provider desktop-auth__provider--quiet"
              onClick={() => void start()}
            >
              <span>Use email, passkey, or another method</span>
            </button>
          </div>
        </>
      )}
      {error ? (
        <p className="desktop-auth__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ── Browser side ──────────────────────────────────────────────────────────

// First leg of a provider-preselected handoff: the tab the app opened with
// ?provider=oauth_… immediately bounces to that provider's OAuth flow (no
// second click), keeping the desktopAuth requestId through the round-trip.
// If launching fails (provider disabled, network), render the fallback —
// the regular Clerk <SignIn/>.
export function DesktopBrowserProviderLaunch({ fallback }: { fallback: ReactNode }) {
  const { signIn, isLoaded } = useSignIn();
  const provider = desktopAuthProvider();
  const requestId = readDesktopAuthParam();
  const [failed, setFailed] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded || !signIn || !provider || !requestId || startedRef.current) return;
    startedRef.current = true;
    // RELATIVE, deliberately — clerk-js forwards `redirect_url` to the Frontend
    // API verbatim (measured: an absolute input arrives absolute on the wire, a
    // path arrives as a path), and Clerk validates ABSOLUTE redirect URLs against
    // the instance's allowed origins. An unlisted origin is rejected with
    // `authorization_invalid` on the way back from the provider — the user lands
    // on clerk.<domain>/v1/oauth_callback?err_code=authorization_invalid and the
    // handoff dies outside our app, where we cannot catch it.
    //
    // A path cannot fail an origin check, which is why the return leg below
    // (DesktopSsoCallback) has always worked while this one did not, and why the
    // plain <SignIn/> card works too — it also redirects by path. Keep these two
    // legs symmetric: if this ever goes back to an absolute URL, desktop provider
    // sign-in breaks on every deployment whose origin is not in allowed_origins.
    const here = `${window.location.pathname}?${PARAM}=${encodeURIComponent(requestId)}`;
    void signIn
      .authenticateWithRedirect({
        strategy: provider,
        redirectUrl: `${here}&${SSO_PARAM}=1`,
        // completed=1 → the landing tab auto-mints (the user JUST picked an
        // account at the provider; asking again would be a dead click).
        redirectUrlComplete: `${here}&${COMPLETED_PARAM}=1`,
      })
      .catch(() => setFailed(true));
  }, [isLoaded, signIn, provider, requestId]);

  if (failed || !provider || !requestId) return <>{fallback}</>;

  const name = PROVIDERS.find((x) => x.strategy === provider)?.name ?? "your provider";
  return (
    <div className="sign-in-status" role="status" aria-live="polite">
      <h1>Opening {name}…</h1>
      <p>Continue in this tab — you’ll be sent back to finish signing in to Steerly.</p>
    </div>
  );
}

// Second leg: Clerk's OAuth callback (?ssoCallback=1). Completes the flow and
// navigates to redirectUrlComplete (the ?desktopAuth=… URL), where the
// signed-in tab mints the app's ticket. The fallback URLs keep the requestId
// even when Clerk has to detour (e.g. a sign-in that becomes a sign-up).
export function DesktopSsoCallback() {
  const requestId = readDesktopAuthParam();
  const here = `${window.location.pathname}?${PARAM}=${encodeURIComponent(requestId ?? "")}&${COMPLETED_PARAM}=1`;
  return (
    <AuthenticateWithRedirectCallback
      signInFallbackRedirectUrl={here}
      signUpFallbackRedirectUrl={here}
    />
  );
}

// Rendered in the system-browser tab the app opened. Once the user is signed
// in here (with their passkey), mint a one-time ticket for the app to claim,
// then tell them they can return to Steerly.
//
// Account binding is NEVER silent for a pre-existing session: if the browser
// was already signed in when this tab opened (and the user didn't just come
// back from an explicit OAuth leg — see COMPLETED_PARAM), ask "Continue as
// <account>?" first. Signing in fresh in THIS tab (embedded form or OAuth
// return) auto-mints — the user just chose the account.
export function DesktopBrowserCompletion({
  preExistingSession,
}: {
  // Lifted to SignInGate (which stays mounted) so a sign-out → sign-in-as-a-
  // different-account flow isn't mis-read as a pre-existing session and
  // re-prompted. Optional: when omitted (e.g. a unit test renders this
  // directly) we fall back to this tab's own capture of isSignedIn. (Gemini G211)
  preExistingSession?: boolean;
} = {}) {
  const requestId = readDesktopAuthParam();
  const { isSignedIn, isLoaded, user } = useUser();
  const clerk = useClerk();
  const mint = useAction(api.auth.desktopAuth.mintTicket);
  const [state, setState] = useState<"signin" | "minting" | "done" | "error">("signin");
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(() => justCompletedOauth());
  const mintedRef = useRef(false);
  // Whether a Clerk session already existed when this tab first knew its auth
  // state. Captured once — flipping to signed-in LATER means the user signed
  // in inside this tab, which is its own consent.
  const preExistingSessionRef = useRef<boolean | null>(null);
  if (isLoaded && preExistingSessionRef.current === null) {
    preExistingSessionRef.current =
      preExistingSession !== undefined ? preExistingSession : !!isSignedIn;
  }
  const needsConfirm = preExistingSessionRef.current === true && !confirmed;

  useEffect(() => {
    if (!isSignedIn || !requestId || mintedRef.current || needsConfirm) return;
    mintedRef.current = true;
    setState("minting");
    (async () => {
      try {
        await mint({ requestId });
        setState("done");
      } catch (e) {
        // Do NOT reset mintedRef here: setState("error") re-renders, and a
        // reset would re-run this effect → mint again → fail again → spam
        // the server in a tight loop. Retry is an explicit user action
        // (the "Try again" button below), which resets mintedRef itself.
        setState("error");
        setError(e instanceof Error ? e.message : "Could not complete the handoff.");
      }
    })();
  }, [isSignedIn, requestId, mint, needsConfirm]);

  const retry = () => {
    mintedRef.current = false;
    setError(null);
    setState("signin");
  };

  if (!isSignedIn) return null; // SignInGate renders the Clerk <SignIn/> (passkeys work here)

  if (needsConfirm) {
    const account =
      user?.primaryEmailAddress?.emailAddress || user?.username || "the signed-in account";
    return (
      <div className="sign-in-gate">
        <div className="sign-in-status" role="status" aria-live="polite">
          <h1>Sign in to the Steerly app?</h1>
          <p>
            This browser is already signed in as <strong>{account}</strong>.
          </p>
          <div className="sign-in-status__actions">
            <button type="button" onClick={() => setConfirmed(true)}>
              Continue as {account}
            </button>
            <button
              type="button"
              onClick={() => {
                // Sign out IN THIS TAB only conceptually — Clerk signOut ends
                // the browser session, after which SignInGate falls back to
                // the provider launch / SignIn form so the user picks the
                // account they actually want in the app.
                void clerk.signOut();
              }}
            >
              Use a different account
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sign-in-gate">
      <div className="sign-in-status" role="status" aria-live="polite">
        {state === "done" ? (
          <>
            <h1>✓ Signed in</h1>
            <p>Return to the Steerly app — it’s signing you in now. You can close this tab.</p>
          </>
        ) : state === "error" ? (
          <>
            <h1>Handoff failed</h1>
            <p>{error}</p>
            <div className="sign-in-status__actions">
              <button type="button" onClick={retry}>
                Try again
              </button>
            </div>
          </>
        ) : (
          <>
            <h1>Completing sign-in…</h1>
            <p>Handing your session back to the Steerly app.</p>
          </>
        )}
      </div>
    </div>
  );
}
